import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { logger } from 'src/analytics/KatalLogger';
import { eErrorMessages } from 'src/constants/generic-constants';
import { getMoment, getTimeDifference } from './date-time-utilities';
import { parseS3URI } from './aws-s3-services';
import { getOnboardSegmentBucketName } from './xpt-s3-bucket-details';
import { retrieveParameterFromSSM } from './ssm-parameter';

let corpSegmentCdnDomain: string | null = null;
let ssmFailureExpiry = 0;
const SSM_FAILURE_TTL_MS = 60_000; // Cache SSM failures for 60s to avoid retry storms

/**
 * Loads the CloudFront CDN domain from SSM Parameter Store.
 * Caches the result so SSM is called only once per session.
 * On failure, caches the empty result for 60s to prevent hammering SSM.
 */
const getCorpSegmentCdnDomain = async (): Promise<string> => {
  if (corpSegmentCdnDomain !== null) {
    return corpSegmentCdnDomain;
  }
  if (Date.now() < ssmFailureExpiry) {
    return '';
  }
  try {
    const stage = ENVIRONMENT_VARIABLES.env.Stage === 'dev' ? 'alpha' : ENVIRONMENT_VARIABLES.env.Stage;
    corpSegmentCdnDomain = await retrieveParameterFromSSM(`/xpt/${stage}/corp-segment-cdn-domain`);
  } catch (error) {
    logger.error('Failed to load corp segment CDN domain from SSM', { error });
    ssmFailureExpiry = Date.now() + SSM_FAILURE_TTL_MS;
    return '';
  }
  return corpSegmentCdnDomain;
};

/**
 * Checks if an S3 URI points to the corp segment bucket and CDN is available.
 */
const CDN_ELIGIBLE_PREFIX = 'corp_segment_json/';

export const isCorpSegmentS3URI = async (s3URI: string): Promise<boolean> => {
  try {
    const { bucket, s3Key } = parseS3URI(s3URI);
    if (bucket !== getOnboardSegmentBucketName()) return false;
    if (!s3Key.startsWith(CDN_ELIGIBLE_PREFIX)) return false;
    const domain = await getCorpSegmentCdnDomain();
    return !!domain;
  } catch {
    return false;
  }
};

// In-flight CDN request deduplication
const inFlightCdnRequests = new Map<string, Promise<JSON>>();

/**
 * Fetches a file from the corp segment CloudFront distribution.
 * No Cognito credentials needed - CloudFront + OAC handles S3 auth.
 */
export const getFileFromCorpSegmentCdn = async (s3URI: string, signal?: AbortSignal): Promise<JSON> => {
  const existing = inFlightCdnRequests.get(s3URI);
  if (existing && !signal) {
    return existing;
  }

  const fetchPromise = fetchFromCdn(s3URI, signal).finally(() => {
    inFlightCdnRequests.delete(s3URI);
  });

  if (!signal) {
    inFlightCdnRequests.set(s3URI, fetchPromise);
  }

  return fetchPromise;
};

const fetchFromCdn = async (s3URI: string, signal?: AbortSignal): Promise<JSON> => {
  const startTime = getMoment();
  const { s3Key } = parseS3URI(s3URI);
  const cdnDomain = await getCorpSegmentCdnDomain();
  const url = `https://${cdnDomain}/${s3Key}`;

  try {
    // 'no-cache' forces the browser to revalidate with CloudFront on every request
    // instead of silently serving stale disk cache. CloudFront still serves from its
    // edge cache when valid — it only fetches from S3 when its cache is invalidated.
    const response = await fetch(url, { signal, cache: 'no-cache' });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(eErrorMessages.NO_DATA_FOUND);
      }
      throw new Error(`CDN fetch failed: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as JSON;
    const duration = getTimeDifference(startTime);
    const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
    logger.info(`CDN file fetched: ${s3Key}`, {
      logType: 'cdn-file-fetch',
      cdnDomain,
      s3Key,
      fileSizeBytes: contentLength,
      durationMs: duration
    });
    return data;
  } catch (error: any) {
    if (signal?.aborted || error.name === 'AbortError') {
      const abortError = new Error('Request was aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }
    if (error.message === eErrorMessages.NO_DATA_FOUND) {
      throw error;
    }
    logger.error('Error fetching file from CDN:', { error, url });
    throw error;
  }
};
